import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './OnboardingPage.css';
import { useUser } from '../../contexts/UserContext';
import { API_BASE_URL } from '../../config';
import DynamicBackground from '../../components/DynamicBackground';
import '../../styles/darkMode.css';
import { COUNTRIES, US_STATES, CANADIAN_PROVINCES, MUSIC_INDUSTRY_ROLES } from '../../constants/countries-states';


const OnboardingPage: React.FC = () => {
  const { user, setUser } = useUser();
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const navigate = useNavigate();

  // Redirect if user has already completed onboarding
  useEffect(() => {
    if (user?.onboarded) {
      navigate('/tools', { replace: true });
    }
  }, [user, navigate]);

  // Reset state when country changes
  useEffect(() => {
    if (country !== 'US' && country !== 'CA') {
      setState('');
    }
  }, [country]);

  const getStateOptions = () => {
    if (country === 'US') {
      return US_STATES;
    } else if (country === 'CA') {
      return CANADIAN_PROVINCES;
    }
    return [];
  };

  const isStateRequired = country === 'US' || country === 'CA';

  if (!user) {
    // If not logged in, redirect to signup
    navigate('/signup');
    return null;
  }

  // Don't render the form if user has already completed onboarding
  if (user.onboarded) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload: any = {
        id: user.id, // Google sub or unique id
        name: user.name,
        email: user.email,
        country,
        role,
      };

      // Only include state if it's required (USA or Canada)
      if (isStateRequired && state) {
        payload.state = state;
      }

      // Only include company if provided
      if (company && company.trim()) {
        payload.company = company.trim();
      }

      const res = await fetch(`${API_BASE_URL}/api/users/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.idToken}`
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to onboard user');
      }
      // Optionally update user context with onboarded: true
      setUser({ 
        ...user, 
        country, 
        state: isStateRequired ? state : undefined, 
        role, 
        company: company.trim() || undefined, 
        onboarded: true 
      });
      navigate('/tools');
    } catch (err: any) {
      setError(err.message || 'Error submitting onboarding info');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DynamicBackground>
      <div className="dark-container">
        <div className="onboarding-header">
          <h2 className="dark-title">Welcome! Tell us about yourself</h2>
          <p className="dark-subtitle">Help us personalize your experience by providing some information about your background</p>
        </div>
        
        <form className="onboarding-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="country" className="form-label">
              Country <span className="required-asterisk">*</span>
            </label>
            <select
              id="country"
              className="dark-select"
              value={country}
              onChange={e => setCountry(e.target.value)}
              required
            >
              {COUNTRIES.map(countryOption => (
                <option key={countryOption.value} value={countryOption.value}>
                  {countryOption.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="state" className="form-label">
              {country === 'CA' ? 'Province/Territory' : 'State'}
              {isStateRequired && <span className="required-asterisk">*</span>}
            </label>
            <select
              id="state"
              className="dark-select"
              value={state}
              onChange={e => setState(e.target.value)}
              disabled={!isStateRequired}
              required={isStateRequired}
            >
              {isStateRequired ? (
                getStateOptions().map(stateOption => (
                  <option key={stateOption.value} value={stateOption.value}>
                    {stateOption.label}
                  </option>
                ))
              ) : (
                <option value="">Not applicable</option>
              )}
            </select>
            {!isStateRequired && (
              <span className="field-hint">State/Province selection is only available for USA and Canada</span>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="role" className="form-label">
              Role in music industry <span className="required-asterisk">*</span>
            </label>
            <select
              id="role"
              className="dark-select"
              value={role}
              onChange={e => setRole(e.target.value)}
              required
            >
              {MUSIC_INDUSTRY_ROLES.map(roleOption => (
                <option key={roleOption.value} value={roleOption.value}>
                  {roleOption.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="company" className="form-label">
              Company name <span className="optional-label">(optional)</span>
            </label>
            <input
              id="company"
              type="text"
              className="dark-input"
              value={company}
              onChange={e => setCompany(e.target.value)}
              placeholder="Enter your company name"
            />
          </div>
          
          <div className="form-group terms-checkbox-group">
            <label className="terms-checkbox-label">
              <input 
                type="checkbox"
                className="terms-checkbox"
                checked={agreeToTerms}
                onChange={e => setAgreeToTerms(e.target.checked)}
                required
              />
              <span className="terms-text">
                I agree to the{' '}
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="terms-link"
                >
                  Terms & Conditions
                </a>
              </span>
            </label>
          </div>
          
          {error && <div className="onboarding-error">{error}</div>}
          
          <button 
            type="submit" 
            className="dark-button primary onboarding-submit-btn"
            disabled={loading || !agreeToTerms}
          >
            {loading ? 'Submitting...' : 'COMPLETE ONBOARDING'}
          </button>
        </form>
      </div>
    </DynamicBackground>
  );
};

export default OnboardingPage; 